import { Chip, Tooltip, Switch } from "@heroui/react";
import {
  Building,
  Eye,
  UsersThree,
  WarningCircle,
  Archive,
  ArrowCounterClockwise,
} from "@phosphor-icons/react";
import { ContractMilestoneDate } from "shared/types/rental-contracts";
import { useEffect, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";

export interface MilestoneSectionProps {
  incomingMilestones: ContractMilestoneDate[];
  onMilestoneUpdate: () => void;
}

const MilestoneSection = ({ incomingMilestones, onMilestoneUpdate }: MilestoneSectionProps) => {
  const [milestones, setMilestones] = useState<ContractMilestoneDate[]>([]);
  const [showHidden, setShowHidden] = useState(false);
  const { session } = useSession();

  useEffect(() => {
    console.log(incomingMilestones);
    setMilestones(incomingMilestones);
  }, [incomingMilestones]);


  const toggleMilestoneArchive = async (milestoneId: number, archive: boolean) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/milestones/${milestoneId}/toggle-archive`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ archive }),
        },
        session
      );

      if (response.ok) {
        onMilestoneUpdate();
        toast.success(archive ? "Milestone verborgen" : "Milestone zichtbaar gemaakt");
      } else {
        toast.error("Er is een fout opgetreden bij het bijwerken van de milestone");
      }
    } catch (error) {
      console.error("Error toggling milestone archive:", error);
      toast.error("Er is een fout opgetreden bij het bijwerken van de milestone");
    }
  };

  const filteredMilestones = showHidden
    ? milestones
    : milestones.filter((milestone) => !milestone.milestoneArchivedFromOverview);

  return (
    <div id="important-events" className="flex flex-col gap-3">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-lg font-medium">
          <WarningCircle weight="duotone" size={20} />
          Belangrijke gebeurtenissen
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm">Toon verborgen</span>
          <Switch
            checked={showHidden}
            color="warning"
            onChange={(e) => setShowHidden(e.target.checked)}
          />
        </div>
      </header>
      <div className="flex flex-col divide-y divide-neutral-primary/10 rounded-lg bg-white shadow-sm">
        {filteredMilestones.map((milestone, index) => (
          <div
            key={index}
            className="flex items-center justify-between gap-3 p-3"
          >
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap gap-2">
                {(milestone as any).unitId && (milestone as any).buildingId && (
                  <Chip
                    className="gap-1"
                    startContent={<Building weight="duotone" size={16} />}
                    classNames={{
                      base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                      content: "px-0 font-medium text-clip overflow-hidden",
                    }}
                  >
                    {(milestone as any).unitId || (milestone as any).buildingId}
                  </Chip>
                )}
                {(milestone as any).tenantName && (
                  <Chip
                    className="gap-1"
                    startContent={<UsersThree weight="duotone" size={18} />}
                    classNames={{
                      base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                      content: "px-0 font-medium text-clip overflow-hidden",
                    }}
                  >
                    {(milestone as any).tenantName}
                  </Chip>
                )}
              </div>
              <p className="text-sm font-medium">
                {milestone.customDescription || milestone.milestoneDateType}{" "}
                op {new Date(milestone.date as Date).toLocaleDateString()}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Tooltip content={milestone.milestoneArchivedFromOverview ? "Milestone zichtbaar maken" : "Milestone verbergen"}>
                <button
                  className="rounded-full border border-neutral-primary/30 p-1"
                  onClick={() => toggleMilestoneArchive(milestone.id as number, !milestone.milestoneArchivedFromOverview)}
                >
                  {milestone.milestoneArchivedFromOverview ? (
                    <ArrowCounterClockwise weight="bold" size={16} />
                  ) : (
                    <Archive weight="bold" size={16} />
                  )}
                </button>
              </Tooltip>
              <Tooltip content="Meer details bekijken">
                <button
                  className="rounded-full border border-neutral-primary/30 p-1"
                  onClick={() => console.log("View details", milestone.id)}
                >
                  <Eye weight="bold" size={16} />
                </button>
              </Tooltip>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MilestoneSection;